import { Component, Input, OnChanges, SimpleChanges, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';

import * as am5index from '@amcharts/amcharts5/index';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import * as am5exporting from '@amcharts/amcharts5/plugins/exporting';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
	standalone: true,
	selector: 'app-time-series-chart',
	imports: [CommonModule, MatButtonModule, TranslateModule, MatIconModule],
	styleUrls: ['./time-series-chart.component.scss'],
	template: `
		<div id="chartdiv" style="width: 100%; height: 300px;"></div>
		<div class="d-flex justify-content-end align-items-center mb-4">
			<button class="filters-show-button " type="button" mat-raised-button color="primary" aria-label="Export chart button" (click)="exportAsImage()">
      <mat-icon class="m-0">download</mat-icon>
			</button>
		</div>
	`,
})
export class TimeSeriesChartComponent implements OnChanges, OnDestroy {
	@Input() data: { [key: string]: any }[] = [];
	@Input() xAxisField: string = 'date';
	@Input() yAxisField: string = 'value';

	private root!: am5index.Root;
	private exporting!: am5exporting.Exporting;

	constructor(private zone: NgZone) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.data && this.data.length > 0 && this.xAxisField && this.yAxisField) {
			this.renderChart();
		}
	}

	renderChart(): void {
		this.disposeChart(); // avoid duplicates

		this.zone.runOutsideAngular(() => {
			let root = am5index.Root.new('chartdiv');
			this.root = root;

			root.dateFormatter.setAll({
				dateFormat: 'dd.MM.yyyy',
				dateFields: [this.xAxisField],
			});

			const myTheme = am5index.Theme.new(root);
			myTheme.rule('AxisLabel', ['minor']).setAll({ dy: 1 });

			root.setThemes([am5themes_Animated.new(root), myTheme, am5themes_Responsive.new(root)]);

			const chart = root.container.children.push(
				am5xy.XYChart.new(root, {
					panX: false,
					panY: false,
					wheelX: 'panX',
					wheelY: 'zoomX',
					paddingLeft: 0,
				})
			);

			const cursor = chart.set(
				'cursor',
				am5xy.XYCursor.new(root, {
					behavior: 'zoomX',
				})
			);
			cursor.lineY.set('visible', false);

			const xAxis = chart.xAxes.push(
				am5xy.DateAxis.new(root, {
					baseInterval: { timeUnit: 'day', count: 1 },
					renderer: am5xy.AxisRendererX.new(root, {
						minorGridEnabled: true,
						minorLabelsEnabled: true,
					}),
					tooltip: am5index.Tooltip.new(root, {}),
          tooltipDateFormat: "dd.MM.yyyy",
				})
			);

			xAxis.setAll({
				dateFormats: {
					day: 'dd.MM.yyyy',
					week: 'dd.MM.yyyy',
					month: 'MM.yyyy',
					year: 'yyyy',
				},
				periodChangeDateFormats: {
					day: 'dd.MM.yyyy',
					week: 'dd.MM.yyyy',
					month: 'MM.yyyy',
					year: 'yyyy',
				},
			});

			const yAxis = chart.yAxes.push(
				am5xy.ValueAxis.new(root, {
					renderer: am5xy.AxisRendererY.new(root, {}),
				})
			);

			const series = chart.series.push(
				am5xy.ColumnSeries.new(root, {
					name: 'Series',
					xAxis,
					yAxis,
					valueYField: this.yAxisField,
					valueXField: this.xAxisField,
					tooltip: am5index.Tooltip.new(root, {
						labelText: `{${this.yAxisField}}`,
					}),
				})
			);
			series.columns.template.setAll({ strokeOpacity: 0 });

			chart.set(
				'scrollbarX',
				am5index.Scrollbar.new(root, {
					orientation: 'horizontal',
				})
			);

			// Handle data format
			const cleanedData = this.data.map(item => ({
				...item,
				[this.xAxisField]: typeof item[this.xAxisField] === 'string' ? new Date(item[this.xAxisField]).getTime() : item[this.xAxisField],
			}));

			series.data.setAll(cleanedData);

			// Animate
			series.appear(1000);
			chart.appear(1000, 100);

			// Exporting Plugin
			this.exporting = am5exporting.Exporting.new(root, {
				// menu: am5exporting.ExportingMenu.new(root, {}),
				pngOptions: {
					quality: 0.8,
					maintainPixelRatio: true,
				},
			});
		});
	}

	exportAsImage() {
		this.exporting.download('png');
	}

	ngOnDestroy(): void {
		this.disposeChart();
	}

	private disposeChart() {
		if (this.root) {
			this.root.dispose();
		}
	}
}
